<template><div><component-combinator component-name="DtCombobox" /><h2 id="base-style" tabindex="-1" class="d-docsite--header-2">Base Style <a class="header-anchor d-link d-docsite--link d-link" href="#base-style" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">A combobox provides accessibility controls and common functionality. It does not render any functioning UI on it's own, but it depends on the elements passed to it via slots.</p>
<p class="d-docsite--paragraph">It has 2 core required slots:</p>
<ol class="d-docsite--ordered-list">
<li class="d-docsite--list-element"><code v-pre>input</code>: contains the input element controlling the list. This will usually be the <RouteLink to="/components/input.html" class="d-docsite--link d-link">Input component</RouteLink>.</li>
<li class="d-docsite--list-element"><code v-pre>list</code>: the list of items to display responding to <code v-pre>input</code>'s value. Almost always this will be a list of <code v-pre>dt-list-item</code>.
This will usually be the <RouteLink to="/components/list-item.html" class="d-docsite--link d-link">List Item component</RouteLink>.</li>
</ol>
<code-example class="d-d-block" source-code='&lt;dt-combobox
  :show-list=&quot;!!value&quot;
  label=&quot;Label Text&quot;
  @escape=&quot;onComboboxEscape&quot;
  @select=&quot;onComboboxSelect&quot;
&gt;
  &lt;template
    #input=&quot;{ inputProps }&quot;
  &gt;
    &lt;dt-input
      placeholder=&quot;Type to show the items&quot;
      v-model=&quot;value&quot;
      v-bind=&quot;inputProps&quot;
    /&gt;
  &lt;/template&gt;
  &lt;template
    #list=&quot;{ listProps }&quot;
  &gt;
    &lt;ol
      v-bind=&quot;listProps&quot;
      class=&quot;d-p-0 d-mbs-100 d-hmx-500 d-of-y-auto&quot;
    &gt;
      &lt;dt-list-item
        v-for=&quot;(item, i) in items&quot;
        :key=&quot;item.id&quot;
        role=&quot;option&quot;
        navigation-type=&quot;arrow-keys&quot;
        @click=&quot;onListItemSelect(i)&quot;
      &gt;
        &lt;template #start&gt;
          &lt;dt-avatar
            :full-name=&quot;(i + 1).toString()&quot;
            :seed=&quot;i.toString()&quot;
          /&gt;
        &lt;/template&gt;
        {{ item.name }}
      &lt;/dt-list-item&gt;
    &lt;/ol&gt;
  &lt;/template&gt;
&lt;/dt-combobox&gt;'>
<dt-combobox
  :show-list="!!value"
  label="Label Text"
  @escape="onComboboxEscape"
  @select="onComboboxSelect"
>
  <template
    #input="{ inputProps }"
  >
    <dt-input
      placeholder="Type to show the items"
      v-model="value"
      v-bind="inputProps"
    />
  </template>
  <template
    #list="{ listProps }"
  >
    <ol
      v-bind="listProps"
      class="d-p-0 d-mbs-100 d-hmx-500 d-of-y-auto"
    >
      <dt-list-item
        v-for="(item, i) in items"
        :key="item.id"
        role="option"
        navigation-type="arrow-keys"
        @click="onListItemSelect(i)"
      >
        <template #start>
          <dt-avatar
            :full-name="(i + 1).toString()"
            :seed="i.toString()"
          />
        </template>
        {{ item.name }}
      </dt-list-item>
    </ol>
  </template>
</dt-combobox>
</code-example>
<h2 id="with-empty-list-item" tabindex="-1" class="d-docsite--header-2">With Empty List Item <a class="header-anchor d-link d-docsite--link d-link" href="#with-empty-list-item" aria-hidden="true">#</a></h2>
<code-example class="d-d-block" source-code='&lt;dt-combobox
  :show-list=&quot;!!value&quot;
  label=&quot;Label Text&quot;
  @escape=&quot;onComboboxEscape&quot;
  @select=&quot;onComboboxSelect&quot;
  :empty-list=&quot;true&quot;
&gt;
  &lt;template
    #input=&quot;{ inputProps }&quot;
  &gt;
    &lt;dt-input
      placeholder=&quot;Type to show the items&quot;
      v-model=&quot;value&quot;
      v-bind=&quot;inputProps&quot;
    /&gt;
  &lt;/template&gt;
  &lt;template #emptyListItem&gt;
    &lt;div class=&quot;d-py-100 d-fc-tertiary&quot;&gt;No matches found.&lt;/div&gt;
  &lt;/template&gt;
&lt;/dt-combobox&gt;'>
<dt-combobox
  :show-list="!!value"
  label="Label Text"
  @escape="onComboboxEscape"
  @select="onComboboxSelect"
  :empty-list="true"
>
  <template
    #input="{ inputProps }"
  >
    <dt-input
      placeholder="Type to show the items"
      v-model="value"
      v-bind="inputProps"
    />
  </template>
  <template #emptyListItem>
    <div class="d-py-100 d-fc-tertiary">No matches found.</div>
  </template>
</dt-combobox>
</code-example>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The combobox input has a role of &quot;combobox&quot;, the list has a role &quot;listbox&quot; and the list items have a role &quot;option&quot;.
Depending on the use case, the user should be able to use <code v-pre>ESC</code> key to hide the list and/or empty the input's value,
but this is not determined by the combobox.</p>
<p class="d-docsite--paragraph">When <code v-pre>loading</code> prop is set to <code v-pre>true</code>, the list also has the <code v-pre>aria-busy</code> attribute set to true.</p>
<p class="d-docsite--paragraph">See <a href="https://www.w3.org/WAI/ARIA/apg/" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">W3C guidelines</a> for more information.</p>
<h3 id="input" tabindex="-1" class="d-docsite--header-3">Input <a class="header-anchor d-link d-docsite--link d-link" href="#input" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The input element should be fully accessible by keyboard. The easiest way to do this is by using an element like a <code v-pre>DtInput</code> that is already accessible. There are some required ARIA attributes for the input element. To make this as straightforward as possible, these ARIA attributes are passed with the correct values as the <code v-pre>inputProps</code> slot prop of the input slot.</p>
<h3 id="list" tabindex="-1" class="d-docsite--header-3">List <a class="header-anchor d-link d-docsite--link d-link" href="#list" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The list element mainly has to contain the correct ARIA attributes, which are passed as <code v-pre>listProps</code> slot prop of the list slot.
The combobox has a prop <code v-pre>listAriaLabel</code> that should be passed as it is used to describe the contents of the list. The list itself can be a Dialtone component or a native HTML element, such as <code v-pre>&lt;ol&gt;&lt;/ol&gt;</code>.</p>
<h3 id="list-item" tabindex="-1" class="d-docsite--header-3">List Item <a class="header-anchor d-link d-docsite--link d-link" href="#list-item" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">The list item should be compatible with keyboard navigation. It is recommended to use <code v-pre>DtListItem</code> component as it supports all the necessary accessibility props and interactions.</p>
<h3 id="focus-keyboard" tabindex="-1" class="d-docsite--header-3">Focus &amp; Keyboard <a class="header-anchor d-link d-docsite--link d-link" href="#focus-keyboard" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">A combobox has well defined standard keyboard interactions that it should support. Only the input element should receive focus and the list items are not focusable. However, while the input has focus <code v-pre>UP</code> and <code v-pre>DOWN</code> arrows move the highlighted selection between the list items. <code v-pre>HOME</code> and <code v-pre>END</code> keys will jump straight to the first or the last item. Once the beginning or the end of the list is reached the selection will move to the opposite end of the list by default. If you want custom behavior you can pass <code v-pre>onBeginningOfList</code> and <code v-pre>onEndOfList</code> methods as props.</p>
<p class="d-docsite--paragraph">Pressing <code v-pre>ESC</code> key while the input has focus will emit an <strong>escape</strong> event that has to be handled based on the use case. Commonly the <code v-pre>ESC</code> key might hide the list or empty the input. <code v-pre>ENTER</code> key will emit a <strong>select</strong> event with the index of the currently highlighted list item. When the highlight selection changes, a <strong>highlight</strong> event is emitted with the index of the currently highlighted item.</p>
<p class="d-docsite--paragraph">When <code v-pre>loading</code> prop is set to <code v-pre>true</code>, only <code v-pre>ESC</code> key will emit an <strong>escape</strong> event.</p>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="combobox" /></div></template>


<script setup>
import { ref } from 'vue';

const value = ref('example');
const items = [
  { name: 'combobox item 1', id: 1 },
  { name: 'combobox item 2', id: 2 },
  { name: 'combobox item 3', id: 3 },
  { name: 'combobox item 4', id: 4 },
  { name: 'combobox item 5', id: 5 },
  { name: 'combobox item 6', id: 6 },
  { name: 'combobox item 7', id: 7 },
  { name: 'combobox item 8', id: 8 },
  { name: 'combobox item 9', id: 9 },
  { name: 'combobox item 10', id: 10 },
  { name: 'combobox item 11', id: 11 },
  { name: 'combobox item 12', id: 12 },
];

function onComboboxSelect (i) {
  value.value = items[i].name;
};

function onListItemSelect (i) {
  value.value = items[i].name;
};

function onComboboxEscape () {
  value.value = '';
};
</script>